import classNames from "classnames";
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./ACAdvancedEllipsisPopover.less";

export interface ACAdvancedEllipsisPopoverProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  popoverClassName?: string;
  popoverMaxWidth?: string | number;
  enterDelay?: number;
  ellipsisReservedWidth?: number;
}

export default function ACAdvancedEllipsisPopover({
  children,
  className,
  style,
  popoverClassName,
  popoverMaxWidth,
  enterDelay = 150,
  ellipsisReservedWidth = 15,
}: ACAdvancedEllipsisPopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const [isTruncated, setIsTruncated] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Check truncation using useLayoutEffect for synchronous measurement
  const checkTruncation = () => {
    const element = contentRef.current;
    const container = containerRef.current;

    if (!element || !container) return;

    const width = container.offsetWidth;
    setContainerWidth(width);

    // Check if content is truncated by comparing scrollWidth with offsetWidth
    const truncated = element.scrollWidth > element.offsetWidth;
    setIsTruncated(truncated);
  };

  useLayoutEffect(() => {
    checkTruncation();

    // Also check after a small delay for async rendered content
    const timeoutId = setTimeout(checkTruncation, 50);

    // Debounced resize handler for both window and container resize
    let resizeTimeoutId: NodeJS.Timeout | null = null;
    const handleDebouncedResize = () => {
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      // Close popover on resize since position becomes stale
      setIsHovering(false);
      resizeTimeoutId = setTimeout(checkTruncation, 50);
    };

    // Listen to window resize
    window.addEventListener("resize", handleDebouncedResize);

    // Observe container resize (handles widget/grid resizing)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      resizeObserver = new ResizeObserver(handleDebouncedResize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      window.removeEventListener("resize", handleDebouncedResize);
      resizeObserver?.disconnect();
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [children]);

  const handleMouseEnter = () => {
    if (isTruncated) {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Show popover after delay
      hoverTimeoutRef.current = setTimeout(() => {
        const element = contentRef.current;
        if (element) {
          const rect = element.getBoundingClientRect();
          setPopoverPos({
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
          });
          setIsHovering(true);
        }
      }, enterDelay);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    // Don't close if moving into the popover
    const related = e.relatedTarget as Node;
    if (related && popoverRef.current?.contains(related)) {
      return;
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    // Small delay to allow popover's onMouseEnter to fire
    hoverTimeoutRef.current = setTimeout(() => setIsHovering(false), 100);
  };

  // Use container width if popoverMaxWidth not provided
  const effectivePopoverWidth =
    popoverMaxWidth !== undefined
      ? popoverMaxWidth
      : `${containerWidth + 12}px`;

  // Calculate the width reduction percentage for dots space
  const contentWidthStyle = isTruncated
    ? { width: `calc(100% - ${ellipsisReservedWidth}px)` }
    : { width: "100%" };

  const content = (
    <div
      ref={containerRef}
      className={classNames("ac-advanced-ellipsis-container")}
      style={{
        position: "relative",
        width: "100%",
        ...style,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Content div with truncation */}
      <div
        ref={contentRef}
        className={classNames("ac-advanced-ellipsis-content", className)}
        style={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          display: "block",
          ...contentWidthStyle,
        }}
      >
        {children}
      </div>

      {/* Manual dots rendered at the end when truncated */}
      {isTruncated && <span className="ac-advanced-ellipsis-dots">…</span>}
    </div>
  );

  // If not truncated, just render content without popover
  if (!isTruncated) {
    return content;
  }

  // Render content with seamless popover on hover
  return (
    <>
      {content}
      {isHovering &&
        popoverPos &&
        createPortal(
          <div
            ref={popoverRef}
            className={classNames(
              "ac-advanced-ellipsis-popover",
              popoverClassName,
            )}
            style={{
              position: "absolute",
              top: `${popoverPos.top - 4}px`,
              left: `${popoverPos.left - 10}px`,
              maxWidth: effectivePopoverWidth,
              zIndex: 1000,
              pointerEvents: "auto",
            }}
            onMouseEnter={() => {
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
              }
              setIsHovering(true);
            }}
            onMouseLeave={handleMouseLeave}
          >
            {/* Full content without truncation */}
            <div className="ac-advanced-ellipsis-popover-content">
              {children}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
